import useEventCallback from '@unifyapps/hooks/useEventCallback';
import _isNil from 'lodash/isNil';
import { type SyntheticEvent, useCallback, useEffect, useState } from 'react';
import type { ListMenuItem, MenuListItem } from '../MenuList';

type Args = {
  enableKeyboardNavigation?: boolean;
  items: MenuListItem[];
  onAction: (id: string, event: SyntheticEvent) => void;
  defaultActiveIndex?: number;
};

export const useKeyboardNavigation = (args: Args) => {
  const { enableKeyboardNavigation, items, onAction, defaultActiveIndex } = args;
  const [hasMouseMoved, setHasMouseMoved] = useState(false);

  // Helper function to check if an item is actionable
  const isActionable = useCallback(
    (index: number) => {
      const item = items[index];
      return item && item.type !== 'divider' && !item.disabled;
    },
    [items],
  );

  // Helper function to find the first actionable item
  const findFirstActionableIndex = useCallback(() => {
    if (!_isNil(defaultActiveIndex)) {
      return defaultActiveIndex;
    }

    for (let i = 0; i < items.length; i++) {
      if (isActionable(i)) {
        return i;
      }
    }
    return -1;
  }, [items, isActionable, defaultActiveIndex]);

  const [activeIndex, setActiveIndex] = useState<number>(() =>
    enableKeyboardNavigation ? findFirstActionableIndex() : -1,
  );

  useEffect(() => {
    if (!enableKeyboardNavigation) {
      return;
    }
    // preserve existing active index if still actionable; otherwise fall back
    // to first actionable. Prevents scrollIntoView from jumping the list to
    // the top when `items` identity changes but the focused row is unchanged
    // (e.g. a row's decorator re-renders after a checkbox toggle).
    setActiveIndex((prev) => {
      if (prev >= 0 && prev < items.length && isActionable(prev)) {
        return prev;
      }
      return findFirstActionableIndex();
    });
  }, [enableKeyboardNavigation, findFirstActionableIndex, isActionable, items.length]);

  // Scroll active item into view when activeIndex changes
  useEffect(() => {
    if (!enableKeyboardNavigation || activeIndex === -1) {
      return;
    }

    // Find the menu item element and scroll it into view
    const menuItem = document.querySelector(`[data-menu-index="${activeIndex}"]`);
    if (menuItem) {
      menuItem.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [activeIndex, enableKeyboardNavigation]);

  const onKeyDown = useEventCallback((event: KeyboardEvent) => {
    if (!enableKeyboardNavigation || items.length === 0) {
      return;
    }
    setHasMouseMoved(false);
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();

      const direction = event.key === 'ArrowDown' ? 1 : -1;
      let nextIndex = activeIndex;

      // Find the next actionable item
      do {
        nextIndex =
          direction === 1
            ? (nextIndex + 1) % items.length
            : (nextIndex - 1 + items.length) % items.length;
      } while (nextIndex !== activeIndex && !isActionable(nextIndex));

      // Only update if we found a different actionable item
      if (isActionable(nextIndex)) {
        setActiveIndex(nextIndex);
      }
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const idx = activeIndex === -1 ? findFirstActionableIndex() : activeIndex;
      if (idx !== -1 && isActionable(idx)) {
        const item = items[idx] as ListMenuItem;
        onAction(item.id, event as unknown as SyntheticEvent);
      }
    }
  });

  useEffect(() => {
    if (!enableKeyboardNavigation) {
      return;
    }
    // TODO: check if there is better place other than document to listen these events
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [enableKeyboardNavigation, onKeyDown]);

  const handleMouseMove = useEventCallback(() => {
    setHasMouseMoved(true);
  });

  useEffect(() => {
    if (!enableKeyboardNavigation) {
      return;
    }
    document.addEventListener('mousemove', handleMouseMove);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, [enableKeyboardNavigation, handleMouseMove]);

  const onMouseEnter = useEventCallback((index: number) => {
    // We need to check if the mouse was moved before setting the active index, because the onMouseEnter event is also triggered
    // when we navigate to overflown item via keyboard and list scrolls, but since this is not a user action we don't want to set the active index.
    // Otherwise on every scroll during keyboard navigation, active index would reset to wherever the mouse pointer was.
    if (enableKeyboardNavigation && hasMouseMoved) {
      setActiveIndex(index);
    }
  });

  return {
    activeIndex,
    onMouseEnter,
  };
};
